// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import * as React from "react";
import { CommandBar, ICommandBarItemProps } from "@fluentui/react/lib/CommandBar";
import { ICustomizations, Customizer } from "@fluentui/react/lib/Utilities";
import { getDarkGreyTheme } from "../common/themes";

interface ICanvasCommandBarProps {
    showLayerMenu?: boolean;
    drawRegionMode?: boolean;
    layers?: any;
    enableDrawRegion?: boolean;
    editMode?: boolean;
    handleZoomIn: () => void;
    handleZoomOut: () => void;
    handleRotateImage: (degrees: number) => void;
    handleLayerChange?: (layer: string) => void;
    handleToggleDrawRegionMode?: () => void;
}

export const CanvasCommandBar: React.FunctionComponent<ICanvasCommandBarProps> = (props: ICanvasCommandBarProps) => {
    const dark: ICustomizations = {
        settings: {
            theme: getDarkGreyTheme(),
        },
        scopedSettings: {},
    };

    let commandBarItems: ICommandBarItemProps[] = [];

    if (props.showLayerMenu) {
        const layerItem: ICommandBarItemProps = {
            key: "layers",
            text: "Layers",
            iconProps: { iconName: "MapLayers" },
            subMenuProps: {
                items: [
                    {
                        key: "text",
                        text: "Text",
                        canCheck: true,
                        iconProps: { iconName: "TextField" },
                        isChecked: props.editMode,
                        onClick: () => props.handleLayerChange("text"),
                    },
                    {
                        key: "table",
                        text: "Tables",
                        canCheck: true,
                        iconProps: { iconName: "Table" },
                        isChecked: props.layers["tables"],
                        onClick: () => props.handleLayerChange("tables"),
                    },
                    {
                        key: "selectionMark",
                        text: "Selection marks",
                        canCheck: true,
                        iconProps: { iconName: "CheckboxComposite" },
                        isChecked: props.layers["checkboxes"],
                        onClick: () => props.handleLayerChange("checkboxes"),
                    },
                    {
                        key: "DrawnRegions",
                        text: "Drawn regions",
                        canCheck: true,
                        iconProps: { iconName: "FieldNotChanged" },
                        isChecked: props.layers["drawnRegions"],
                        className: props.drawRegionMode ? "disabled" : "",
                        onClick: () => props.handleLayerChange("drawnRegions"),
                        disabled: props.drawRegionMode
                    },
                    {
                        key: "Label",
                        text: "Labels",
                        canCheck: true,
                        iconProps: { iconName: "Label" },
                        isChecked: props.layers["label"],
                        onClick: () => props.handleLayerChange("label"),
                    },
                ],
            },
        };
        commandBarItems = [
            layerItem,
            // {
            //     key: "drawRegion",
            //     text: "Draw region",
            //     iconProps: { iconName: "FieldNotChanged" },
            //     toggle: true,
            //     checked: props.drawRegionMode,
            //     className: !props.layers["drawnRegions"] ? "disabled" : "",
            //     onClick: () => props.handleToggleDrawRegionMode(),
            //     disabled: !props.layers["drawnRegions"],
            // },
            {
                key: "enterEditMode",
                text: "Edit Mode",
                toggle: true,
                checked: props.editMode,
                onClick: () => props.handleLayerChange("text"),
            }
        ];
        if (!props.enableDrawRegion) {
            layerItem.subMenuProps.items = layerItem.subMenuProps.items.filter(item => item.key !== "DrawnRegions");
            commandBarItems = [...commandBarItems.filter(item => item.key !== "drawRegion")];
        }
    }

    const commandBarFarItems: ICommandBarItemProps[] = [
        {
            key: "Rotate90CounterClockwise",
            text: "Rotate 90 Counter Clockwise",
            // This needs an ariaLabel since it's icon-only
            ariaLabel: "Rotate 90 Counter Clockwise",
            iconOnly: true,
            iconProps: { iconName: "Rotate90CounterClockwise" },
            onClick: () => props.handleRotateImage(-90),
        },
        {
            key: "Rotate90Clockwise",
            text: "Rotate 90 Clockwise",
            // This needs an ariaLabel since it's icon-only
            ariaLabel: "Rotate 90 Clockwise",
            iconOnly: true,
            iconProps: { iconName: "Rotate90Clockwise" },
            style: { marginRight: "1rem" },
            onClick: () => props.handleRotateImage(90),
        },
        {
            key: "zoomOut",
            text: "Zoom out",
            // This needs an ariaLabel since it's icon-only
            ariaLabel: "Zoom out",
            iconOnly: true,
            iconProps: { iconName: "ZoomOut" },
            onClick: () => props.handleZoomOut(),
        },
        {
            key: "zoomIn",
            text: "Zoom in",
            // This needs an ariaLabel since it's icon-only
            ariaLabel: "Zoom in",
            iconOnly: true,
            iconProps: { iconName: "ZoomIn" },
            onClick: () => props.handleZoomIn(),
        }
    ];

    return (
        <Customizer {...dark}>
            <CommandBar
                items={commandBarItems}
                farItems={commandBarFarItems}
                ariaLabel="Use left and right arrow keys to navigate between commands"
            />
        </Customizer>
    );
};
